'use client'

import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {ClassAssistClassManuals} from '@/components/class-assist/class/ClassAssistClassManuals'
import {ClassAssistClassOverview} from '@/components/class-assist/class/ClassAssistClassOverview'
import {GetStartedWithVerificationBanner} from '@/components/common/banner/GetStartedWithVerificationBanner'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {Tile} from '@/components/common/tiles/Tile'
import {Page} from '@/components/Page'
import {ServiceRequestTiles} from '@/components/service-request/ServiceRequestTiles'
import {getClassAssistClassContent, getClassAssistContent} from '@/content/classAssist'
import {useTranslation} from '@/i18n'
import {ClassAssistClass} from '@/utils/class-assist/types'
import {isPartiallySupportedLocale} from '@/utils/constants'
import {getGSClassNamesByLanguage} from '@/utils/goods-and-services/classNames'
import {useLanguage} from '@/utils/i18n/language'
import {analytics} from '@/utils/tracking/analytics'

type ClassAssistClassPageProps = {
  breadcrumbTrail: BreadcrumbStep[]
  classCode: string
  classData: Omit<ClassAssistClass, 'relatedItems'>
  items: {name: string; slug: string}[]
  associatedClassCodes: string[]
}

const ClassAssistClassPage: FC<ClassAssistClassPageProps> = ({
  breadcrumbTrail,
  classCode,
  classData,
  items,
  associatedClassCodes,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {hero, description} = getClassAssistClassContent(classCode)
  const {headerLabel, header, tiles, getStartedBanner} = getClassAssistContent(language)

  const isPartiallySupported = isPartiallySupportedLocale(language)
  const classNames = getGSClassNamesByLanguage(language)

  return (
    <>
      <Page
        navigationLabel={isPartiallySupported ? t(headerLabel) : undefined}
        hasGetHelpButton={!isPartiallySupported}
        hasRegionalSettings={!isPartiallySupported}
        hasLogin={!isPartiallySupported}
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () =>
            analytics.track('Click on Start registration (Class assist - Header)', {classCode}),
        }}
      >
        <Hero
          title={t(hero.title, {classCode, className: classNames[Number(classCode)]})}
          subtitle={t(hero.subtitle, {classCode})}
          breadcrumbs={<Breadcrumbs trail={breadcrumbTrail} hideCurrent />}
          maxWidth="100%"
        />
        <Section hasPaddingTop>
          <ClassAssistClassOverview
            classCode={classCode}
            description={t(description)}
            associatedClassCodes={associatedClassCodes}
            relatedSectors={classData.relatedSectors}
            items={items}
            examples={classData.examples}
          />
        </Section>
        <Section hasPaddingTop>
          <ClassAssistClassManuals classCode={classCode} links={classData.links} />
        </Section>
        <Section hasPaddingTop>
          <ServiceRequestTiles title={t(tiles.title)}>
            {tiles.list.map(({title, description, imageSrc, imageAlt}, i) => (
              <Tile
                key={i}
                title={t(title)}
                description={t(description)}
                imageSrc={imageSrc}
                imageAlt={imageAlt}
              />
            ))}
          </ServiceRequestTiles>
        </Section>
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={getStartedBanner.buttonLink}
          primaryButtonText={t(getStartedBanner.buttonText)}
          primaryButtonTrackedEvent="GetStartedBanner-ClassAssistGsClassPage"
          hasPaddingTop
        />
      </Page>
    </>
  )
}

export default ClassAssistClassPage
